class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');

    setTimeout(() => {
      enforceSupplementCartLimits();
      disableMaxedCartButtons();
    }, 150);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    cartDrawerChanged();

    if (this.querySelector('.drawer__inner').classList.contains('is-empty')) {
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      this.open();

      setTimeout(() => {
        enforceSupplementCartLimits();
        disableMaxedCartButtons();
      }, 150);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

let cartLimitValidationRunning = false;
let cartLimitValidationRerun = false;

// Side cart redraws (INT-525). An add draws the side cart from its own answer (CartDrawer.renderContents) and a
// quantity save from its /cart/change answer (CartItems.updateQuantity). Every other redraw fetches the side cart
// first: the check after an add, the refetch after another script changed the cart, the limit refresh, the free gift. On a slow phone that
// answer could land after the shopper tapped + again: it painted the older cart back (3 snapped back to 1, Afzal)
// and swapped out the quantity box while its save was on the way, so the saved total never showed and - did nothing
// (Green Tea, Pelvic Wand). So such a redraw takes a ticket before it fetches and paints only if nothing changed
// meanwhile and no tap or save is waiting; otherwise it drops its answer and one fresh redraw runs once it is quiet.
const cartDrawerSync = {
  generation: 0, // moves on every tap, save and redraw
  saves: new Set(), // quantity saves sent and not answered yet ({ at })
  taps: new Map(), // side cart element -> when a quantity changed; CartItems saves it ON_CHANGE_DEBOUNCE_TIMER later
  catchUp: null, // the queued fresh redraw
};

function cartDrawerChanged() {
  cartDrawerSync.generation += 1;
}

function cartDrawerQuiet() {
  const now = Date.now();

  // A tap whose save never started stops counting after 5 s, a save with no answer after 15 s.
  for (const [items, at] of cartDrawerSync.taps) {
    if (now - at < 5000) return false;
    cartDrawerSync.taps.delete(items);
  }

  for (const save of cartDrawerSync.saves) {
    if (now - save.at < 15000) return false;
    cartDrawerSync.saves.delete(save);
  }

  return true;
}

function cartDrawerTicket() {
  return cartDrawerSync.generation;
}

// Whether a redraw fetched under `ticket` may paint now. If not, its answer is dropped and one fresh redraw is queued
// for when the side cart is quiet, so the side cart always ends on the saved cart.
function cartDrawerMayPaint(ticket) {
  if (ticket === cartDrawerSync.generation && cartDrawerQuiet()) return true;

  queueCartDrawerCatchUp();
  return false;
}

function queueCartDrawerCatchUp() {
  if (cartDrawerSync.catchUp) return;

  cartDrawerSync.catchUp = setTimeout(() => {
    cartDrawerSync.catchUp = null;

    if (!cartDrawerQuiet()) {
      queueCartDrawerCatchUp();
      return;
    }

    redrawCartDrawer().catch((e) => {
      console.error(e);
    });
  }, ON_CHANGE_DEBOUNCE_TIMER);
}

// The pre-checkout pop-up (theme setting, for tagged products) sits beside .drawer__inner, and on creation hooks the
// Checkout button inside it. A redraw replaces that button, so the pop-up is redrawn too: the new button opens it, and
// it comes and goes with the tagged products. One the shopper has open stays open.
function redrawCartDrawerPopup(cartDrawer, parsedCartDrawer) {
  const drawerRoot = cartDrawer.querySelector('#CartDrawer');
  const parsedRoot = parsedCartDrawer.querySelector('#CartDrawer');
  if (!drawerRoot || !parsedRoot) return;

  const popup = drawerRoot.querySelector(':scope > cart-drawer-popup');
  const parsedPopup = parsedRoot.querySelector(':scope > cart-drawer-popup');
  const wasOpen = !!popup && !popup.classList.contains('close');

  if (popup) popup.remove();
  if (!parsedPopup) return;

  if (wasOpen) parsedPopup.classList.remove('close');
  drawerRoot.insertAdjacentHTML('beforeend', parsedPopup.outerHTML);
}

// Fetches the side cart and cart bubble for this page, as a quantity save does, and paints them unless a tap, save or
// newer redraw got there first. Resolves true when it painted.
function redrawCartDrawer() {
  const ticket = cartDrawerTicket();

  return fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`)
    .then((response) => {
      if (!response.ok) throw new Error(`side cart sections: HTTP ${response.status}`);
      return response.json();
    })
    .then((sections) => {
      if (!cartDrawerMayPaint(ticket)) return false;

      const cartDrawer = document.querySelector('cart-drawer');
      const drawerInner = cartDrawer && cartDrawer.querySelector('.drawer__inner');
      const parsedCartDrawer =
        sections['cart-drawer'] &&
        new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('cart-drawer');
      const parsedDrawerInner = parsedCartDrawer && parsedCartDrawer.querySelector('.drawer__inner');

      if (drawerInner && parsedDrawerInner) {
        const isEmpty = parsedCartDrawer.classList.contains('is-empty');
        const hadFocus = cartDrawer.contains(document.activeElement);

        drawerInner.innerHTML = parsedDrawerInner.innerHTML;
        drawerInner.classList.toggle('is-empty', isEmpty);
        cartDrawer.classList.toggle('is-empty', isEmpty);
        redrawCartDrawerPopup(cartDrawer, parsedCartDrawer);

        // The focused control was replaced: keep focus in the open drawer, as CartDrawer.open() does.
        const focusContainer = isEmpty
          ? cartDrawer.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        if (hadFocus && focusContainer && cartDrawer.classList.contains('active')) {
          trapFocus(focusContainer, drawerInner);
        }
      }

      const cartIconBubble = document.getElementById('cart-icon-bubble');
      const parsedCartIcon =
        sections['cart-icon-bubble'] &&
        new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');

      if (cartIconBubble && parsedCartIcon) {
        cartIconBubble.innerHTML = parsedCartIcon.innerHTML;
      }

      cartDrawerChanged();
      disableMaxedCartButtons();
      return true;
    });
}

// An add (product form, kit pages, quick add, sticky button, upsell app) publishes its /cart/add answer and draws the
// side cart from it with CartDrawer.renderContents. That answer can be older than the cart: the write gate
// (snippets/cart-write-gate.liquid) can hold it while a quantity save lands. So one fresh redraw follows once quiet,
// rather than straight away, where it would only race the shopper's taps.
function cartUpdateBroughtDrawer(event) {
  const sections = event && event.cartData && event.cartData.sections;
  return !!sections && typeof sections['cart-drawer'] === 'string';
}

function getCartItemMaxQtyFromText(cartItemText) {
  const text = cartItemText.toLowerCase();

  // Same cap as getCartItemMaxQtyFromCartItem: every Berberine product, up to 3 per order, so + greys out
  // instead of adding a unit the cart then takes back (INT-525; 3 per order: Javier via Afzal, 10-28).
  if (text.includes('berberine')) {
    return 3;
  }

  const isSubscriptionText =
    text.includes('delivery every') ||
    text.includes('every ') ||
    text.includes('subscription');

  const isSingle =
    text.includes('single');

  const isThreePack =
    text.includes('3-pack') ||
    text.includes('3 pack') ||
    text.includes('3pack');

  if (isSubscriptionText && isSingle) {
    return 1;
  }

  if (isSubscriptionText && isThreePack) {
    return 1;
  }

  if (!isSubscriptionText && isSingle) {
    return 3;
  }

  if (!isSubscriptionText && isThreePack) {
    return 4;
  }

  return null;
}

// Berberine is capped per product, across its one-time and Subscribe & Save lines: up to 3 per order (Javier via
// Afzal, 10-28, INT-525). Every other cap is per line.
function isBerberineCartItem(item) {
  const productTitle = item.product_title ? item.product_title.toLowerCase() : '';

  return item.sku === 'IR-059' || productTitle.includes('berberine');
}

function getCartItemMaxQtyFromCartItem(item) {
  const variantTitle = item.variant_title ? item.variant_title.toLowerCase() : '';
  const isSubscription = !!item.selling_plan_allocation;

  if (isBerberineCartItem(item)) {
    return 3;
  }

  if (isSubscription && variantTitle.includes('single')) {
    return 1;
  }

  if (
    isSubscription &&
    (
      variantTitle.includes('3-pack') ||
      variantTitle.includes('3 pack') ||
      variantTitle.includes('3pack')
    )
  ) {
    return 1;
  }

  if (!isSubscription && variantTitle.includes('single')) {
    return 3;
  }

  if (
    !isSubscription &&
    (
      variantTitle.includes('3-pack') ||
      variantTitle.includes('3 pack') ||
      variantTitle.includes('3pack')
    )
  ) {
    return 4;
  }

  return null;
}

// The + limit of one cart line: its cap and how many units count against it (for Berberine, every line of the same
// product in that cart). Null when the line has no cap.
function cartLineLimit(cartItem) {
  const itemText = cartItem.innerText || '';
  const maxQty = getCartItemMaxQtyFromText(itemText);
  const quantityInput = cartItem.querySelector('input.quantity__input, input[name="updates[]"]');

  if (maxQty === null || !quantityInput) return null;

  const variantId = quantityInput.dataset.quantityVariantId;
  const cartList = cartItem.closest('cart-drawer-items, cart-items');
  const perProduct = itemText.toLowerCase().includes('berberine') && !!variantId && !!cartList;
  const countedInputs = perProduct
    ? Array.from(cartList.querySelectorAll('input[data-quantity-variant-id]')).filter(
        (input) => input.dataset.quantityVariantId === variantId
      )
    : [quantityInput];
  const counted = countedInputs.reduce((sum, input) => sum + (parseInt(input.value, 10) || 0), 0);

  return { maxQty, counted, perProduct, quantityInput };
}

function disableMaxedCartButtons() {
  document.querySelectorAll('.cart-item, [id^="CartDrawer-Item"]').forEach((cartItem) => {
    const limit = cartLineLimit(cartItem);
    const plusButton = cartItem.querySelector('[name="plus"], .quantity__button[name="plus"]');

    if (!limit || !plusButton) return;

    if (limit.counted >= limit.maxQty) {
      plusButton.setAttribute('disabled', 'disabled');
      plusButton.classList.add('disabled');
      // A per-line cap also shows the cap in the box; a per-product cap leaves each line its own quantity.
      if (!limit.perProduct) limit.quantityInput.value = limit.maxQty;
    } else {
      plusButton.removeAttribute('disabled');
      plusButton.classList.remove('disabled');
    }
  });
}

document.addEventListener(
  'click',
  function (event) {
    const plusButton = event.target.closest('[name="plus"], .quantity__button[name="plus"]');

    if (!plusButton) return;

    const cartItem = plusButton.closest('.cart-item, [id^="CartDrawer-Item"]');

    if (!cartItem) return;

    const limit = cartLineLimit(cartItem);

    if (limit && limit.counted >= limit.maxQty) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!limit.perProduct) limit.quantityInput.value = limit.maxQty;
      plusButton.setAttribute('disabled', 'disabled');
      plusButton.classList.add('disabled');

      return false;
    }
  },
  true
);

async function enforceSupplementCartLimits() {
  if (cartLimitValidationRunning) {
    cartLimitValidationRerun = true;
    return;
  }

  cartLimitValidationRunning = true;

  try {
    const ticket = cartDrawerTicket();
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    // Capped lines, grouped by what their cap counts. Shopify lists the newest line first, so the line that went
    // over gives way first.
    const groups = new Map();

    for (const item of cart.items) {
      const maxQty = getCartItemMaxQtyFromCartItem(item);

      if (maxQty === null) continue;

      const groupKey = isBerberineCartItem(item) ? `variant:${item.variant_id}` : `line:${item.key}`;

      if (!groups.has(groupKey)) groups.set(groupKey, { maxQty, lines: [] });
      groups.get(groupKey).lines.push(item);
    }

    const writes = [];

    for (const { maxQty, lines } of groups.values()) {
      let excess = lines.reduce((sum, line) => sum + line.quantity, 0) - maxQty;

      for (const line of lines) {
        if (excess <= 0) break;

        const quantity = Math.max(0, line.quantity - excess);

        excess -= line.quantity - quantity;
        writes.push({ id: line.key, quantity });
      }
    }

    let changed = false;

    for (const write of writes) {
      // The cart was read before a tap, save or redraw that came after: read it again. A save still on its way
      // checks the limit itself once it is answered.
      if (ticket !== cartDrawerTicket() || !cartDrawerQuiet()) {
        if (cartDrawerQuiet()) cartLimitValidationRerun = true;
        break;
      }

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(write),
      });

      changed = true;
    }

    if (changed) {
      await refreshCartDrawerAfterLimitChange();
    }

    disableMaxedCartButtons();
  } catch (error) {
    console.error('Cart limit validation error:', error);
  } finally {
    cartLimitValidationRunning = false;

    if (cartLimitValidationRerun) {
      cartLimitValidationRerun = false;
      enforceSupplementCartLimits();
    }
  }
}

async function refreshCartDrawerAfterLimitChange() {
  try {
    // A tap, save or newer redraw since the limit change wins; the side cart then redraws once it is quiet.
    if (!(await redrawCartDrawer())) return;

    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      cartDrawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    }
  } catch (error) {
    console.error('Cart drawer refresh error:', error);
    window.location.reload();
  }
}

function runCartLimitCheckWithDelay() {
  setTimeout(() => {
    enforceSupplementCartLimits();
    disableMaxedCartButtons();
  }, 150);
}

document.addEventListener('click', function (event) {
  if (
    event.target.closest('quantity-input') ||
    event.target.closest('.quantity') ||
    event.target.closest('[name="plus"]') ||
    event.target.closest('[name="minus"]') ||
    event.target.closest('.quantity__button')
  ) {
    runCartLimitCheckWithDelay();
  }
});

document.addEventListener('change', function (event) {
  if (
    event.target.matches('input[name="updates[]"]') ||
    event.target.closest('quantity-input') ||
    event.target.classList.contains('quantity__input')
  ) {
    runCartLimitCheckWithDelay();
  }
});

document.addEventListener('DOMContentLoaded', function () {
  setTimeout(() => {
    enforceSupplementCartLimits();
    disableMaxedCartButtons();
  }, 150);
});

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();

    // CartItems saves a changed quantity ON_CHANGE_DEBOUNCE_TIMER later; until then no other redraw may swap these
    // quantity boxes out (INT-525).
    this.addEventListener('change', () => {
      cartDrawerSync.taps.set(this, Date.now());
      cartDrawerChanged();
    });
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }

      if (cartUpdateBroughtDrawer(event)) {
        queueCartDrawerCatchUp();
        this.refreshPrescriptions();
        return;
      }

      this.onCartUpdate();
    });
  }

  // CartItems.onCartUpdate, with the side cart redraw behind the ticket (redrawCartDrawer).
  onCartUpdate(prescriptionsUpdate = true) {
    redrawCartDrawer().catch((e) => {
      console.error(e);
    });

    if (prescriptionsUpdate) {
      this.refreshPrescriptions();
    }
  }

  // Works in conjunction with 'product-file-upload.liquid'. Used for prescriptions.
  refreshPrescriptions() {
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((response) => {
        this.updatePrescriptions(response.items, response);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  onChange(event) {
    cartDrawerSync.taps.delete(this);
    super.onChange(event);
  }

  // Other redraws wait while a save is on its way; once it is answered, the limit is checked on the saved cart.
  updateQuantity(...args) {
    const save = { at: Date.now() };
    // CartItems.updateQuantity numbers its save first thing, so this is the number it is about to take.
    const saveOrder = cartItemsSaveOrder(this);
    const saveNumber = saveOrder.sent + 1;

    cartDrawerChanged();
    cartDrawerSync.saves.add(save);

    const settle = () => {
      cartDrawerSync.saves.delete(save);
      cartDrawerChanged();
      // No answer arrived (the save failed on the network): the side cart may show a quantity that was never saved,
      // so it redraws the saved cart once quiet.
      if (saveOrder.answered < saveNumber) queueCartDrawerCatchUp();
      disableMaxedCartButtons();
      runCartLimitCheckWithDelay();
    };

    let saving;

    try {
      saving = super.updateQuantity(...args);
    } catch (error) {
      settle();
      throw error;
    }

    if (saving && typeof saving.then === 'function') {
      saving.then(settle, settle);
    } else {
      settle();
    }

    return saving;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
